import TextInput from './TextInput';
import { useClientsQuery } from '../lib/Queries';
import { useContext, useState } from 'react';
import { ErrorContext } from '../lib/ErrorContext';
import TextInputWithInlineLabel from './TextInputWithInlineLabel.jsx';
import MagnifyingGlassIcon from './icons/MagnifyingGlassIcon.jsx';
import PencilSquareIcon from './icons/PencilSquareIcon';
import { XCircleIcon } from '@heroicons/react/24/solid';
import SidePanel from './SidePanel.jsx';

const ClientPicker = ({ value, setValue, setTimezone, initialSearch }) => {
    const [search, setSearch] = useState(initialSearch);
    const [isNewClient, setIsNewClient] = useState(false);
    const [showClientResults, setShowClientResults] = useState(false);
    const { data, isLoading } = useClientsQuery();
    const clients =
        (search &&
            (data || [])
                .filter(c => {
                    const key = `${c.email} ${c.phone} ${c.first_name} ${c.last_name}`;
                    return key.toLowerCase().indexOf(search.toLowerCase()) > -1;
                })
                .slice(0, 8)) ||
        [];

    const [newClient, setNewClient] = useState({});

    function isSelected(client) {
        return (
            value?.email === client.email &&
            value?.firstName === client.first_name &&
            value?.lastName === client.last_name
        );
    }

    const noClientsMatch =
        !data?.length || // brand new, no clients at all
        (clients.length === 0 && search); // No clients matched
    const hasClients = data?.length > 0;
    const showClientFields = isNewClient;
    const showSearch = showClientFields || !value.email;

    return (
        <div>
            <div className="mb-4">
                <>
                    {showSearch && hasClients && (
                        <TextInputWithInlineLabel
                            prefix={
                                <MagnifyingGlassIcon className="w-5 h-5 text-primary-700" />
                            }
                            inputClassName={`pl-10`}
                            value={search || ''}
                            onChange={setSearch}
                            onFocus={() => setShowClientResults(true)}
                            // onBlur={() => setShowClientResults(false)}
                            placeholder="Search existing clients..."
                        />
                    )}

                    {!showClientFields && !clients?.length && (
                        <button
                            type="button"
                            className="btn-light-pill mt-2"
                            onClick={() => setIsNewClient(true)}
                        >
                            Add New Client
                        </button>
                    )}

                    {showClientResults && (
                        <div className="panel shadow-lg mt-1">
                            {!clients ||
                                (clients.length === 0 && search && (
                                    <div className="text-gray-500 p-4">
                                        No results for "{search}"
                                    </div>
                                ))}
                            {clients.map(client => (
                                <button
                                    key={client.id}
                                    type="button"
                                    onClick={ev => {
                                        ev.preventDefault();
                                        setShowClientResults(false);

                                        if (typeof setTimezone === 'function') {
                                            setTimezone(client.timezone);
                                        }

                                        setValue({
                                            email: client.email,
                                            firstName: client.first_name,
                                            lastName: client.last_name,
                                            phone: client.phone,
                                            clientTimezone: client.timezone,
                                        });
                                    }}
                                    className={`group w-full block text-left px-4 py-2 hover:bg-gray-100 ${isSelected(client) ? 'border-l-2 pl-2 border-primary-700' : ''}`}
                                >
                                    <div className="group-hover:underline font-semibold">
                                        {client.first_name} {client.last_name}
                                    </div>
                                    <div className="text-sm">
                                        {client.email}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </>
            </div>

            {!showSearch && (
                <div className="panel p-4 flex items-center justify-between">
                    <div>
                        <div className="font-semibold">
                            {value.firstName} {value.lastName}
                        </div>
                        <div>{value.email}</div>
                    </div>
                    <div>
                        <button
                            onClick={() => {
                                setValue({
                                    firstName: '',
                                    lastName: '',
                                    email: '',
                                    phone: '',
                                });
                            }}
                            className="py-4 pl-4"
                        >
                            <XCircleIcon className="w-5 h-5 text-red-500 hover:text-red-700" />
                        </button>
                    </div>
                </div>
            )}

            <SidePanel
                isOpen={showClientFields}
                onClose={() => {
                    // Clear it out
                    setIsNewClient(false);
                    setSearch('');
                }}
                title="Add New Client"
                showSave={true}
                saveLabel="Add"
                onCancel={() => setIsNewClient(false)}
                onSave={() => {
                    setValue(newClient);
                    setNewClient({});

                    setIsNewClient(false);
                    setSearch('');
                }}
            >
                {showClientFields && (
                    <>
                        <div className="flex mb-4">
                            <TextInput
                                id="client.firstName"
                                label="First Name"
                                autoFocus={true}
                                value={newClient?.firstName || ''}
                                onChange={v =>
                                    setNewClient({ ...newClient, firstName: v })
                                }
                            />

                            <TextInput
                                id="client.lastName"
                                label="Last Name"
                                className="ml-2"
                                value={newClient?.lastName || ''}
                                onChange={v =>
                                    setNewClient({ ...newClient, lastName: v })
                                }
                            />
                        </div>

                        <TextInput
                            id="client.email"
                            label="Email"
                            className="mb-4"
                            required={true}
                            value={newClient?.email || ''}
                            onChange={v =>
                                setNewClient({ ...newClient, email: v })
                            }
                        />

                        <TextInput
                            id="client.phone"
                            label="Phone"
                            value={newClient?.phone || ''}
                            onChange={v =>
                                setNewClient({ ...newClient, phone: v })
                            }
                        />
                    </>
                )}
            </SidePanel>
        </div>
    );
};

export default ClientPicker;
